import React, { useState } from "react";
import {
  buildEmailValue,
  buildLinkValue,
  buildLongTextValue,
  buildNumbersValue,
  buildPhoneValue,
  buildTextValue,
} from "../buildColumnValue";
import { EDITOR_INPUT_CLASS, EditorFrame, EditorProps } from "./EditorFrame";

/** text, numbers and phone: one input. text and numbers store a plain string value. */
export const SingleInputEditor = ({
  type,
  state,
  onCommit,
  onCancel,
}: EditorProps & { type: "text" | "numbers" | "phone" }) => {
  const initial =
    type === "phone"
      ? (state.value?.phone ?? "")
      : typeof state.value === "string" || typeof state.value === "number"
        ? String(state.value)
        : (state.text ?? "");
  const [draft, setDraft] = useState(initial);

  const build = () => {
    if (type === "numbers") return buildNumbersValue(draft);
    if (type === "phone") return buildPhoneValue(draft, state.value?.countryShortName);
    return buildTextValue(draft);
  };

  return (
    <EditorFrame build={build} dirty={draft !== initial} onCommit={onCommit} onCancel={onCancel}>
      <input
        autoFocus
        aria-label="Column value"
        className={EDITOR_INPUT_CLASS}
        inputMode={type === "numbers" ? "decimal" : type === "phone" ? "tel" : undefined}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </EditorFrame>
  );
};

export const LongTextEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const initial: string = state.value?.text ?? state.text ?? "";
  const [draft, setDraft] = useState(initial);

  return (
    <EditorFrame
      multiline
      dirty={draft !== initial}
      build={() => buildLongTextValue(draft)}
      onCommit={onCommit}
      onCancel={onCancel}
    >
      <textarea
        autoFocus
        aria-label="Column value"
        className={`${EDITOR_INPUT_CLASS} h-auto min-h-[120px] py-1 resize-y`}
        value={draft}
        // Open with the cursor after the text, ready to add to it.
        onFocus={(event) => {
          const end = event.target.value.length;
          event.target.setSelectionRange(end, end);
        }}
        onChange={(event) => setDraft(event.target.value)}
      />
    </EditorFrame>
  );
};

/** email and link: the target plus the text monday shows in its place. */
export const TargetAndTextEditor = ({
  type,
  state,
  onCommit,
  onCancel,
}: EditorProps & { type: "email" | "link" }) => {
  const targetKey = type === "email" ? "email" : "url";
  const initialTarget: string = state.value?.[targetKey] ?? "";
  const initialText: string =
    state.value?.text && state.value.text !== initialTarget ? state.value.text : "";
  const [target, setTarget] = useState(initialTarget);
  const [text, setText] = useState(initialText);
  const dirty = target !== initialTarget || text !== initialText;

  const build = () =>
    type === "email" ? buildEmailValue(target, text) : buildLinkValue(target, text);

  return (
    <EditorFrame build={build} dirty={dirty} onCommit={onCommit} onCancel={onCancel}>
      <input
        autoFocus
        aria-label={type === "email" ? "Email address" : "Web address"}
        placeholder={type === "email" ? "name@company.com" : "https://"}
        type={type === "email" ? "email" : "url"}
        className={EDITOR_INPUT_CLASS}
        value={target}
        onChange={(event) => setTarget(event.target.value)}
      />
      <input
        aria-label="Text to display"
        placeholder="Text to display"
        className={EDITOR_INPUT_CLASS}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
    </EditorFrame>
  );
};
